import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Typography, Button, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Stack, IconButton,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { GT_COLORS } from '../../../../components/groups-teams/shared/constants';
import { ScheduleService } from '../../../../services/schedule.service';
import { ScheduleType } from '../../../../types/schedule.types';
import { RootState } from '../../../../store';
import { toast } from 'react-toastify';

/* ─── Props ─────────────────────────────────────────────── */

interface EmergencyScheduleSectionProps {
    teamId: string;
    teamName: string;
    defaultContacts?: { full_name: string; phone_number: string }[];
}

/* ─── Types ─────────────────────────────────────────────── */

type ShiftKind = 'day' | 'overnight';

interface Period {
    label: string;
    start: number;
    end: number;
    shift: ShiftKind;
}

type OptBlock = ScheduleType.Model['optimizedBlocks'][number];

/* ─── Constants ─────────────────────────────────────────── */

const PERIODS: Period[] = [
    { label: '12–9 AM', start: 0, end: 9, shift: 'overnight' },
    { label: '9AM–5PM', start: 9, end: 17, shift: 'day' },
    { label: '5PM–12AM', start: 17, end: 24, shift: 'overnight' },
];

const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
/** Schedule uses dayOfWeek 1-7 (Mon=1…Sun=7). */
const DOW_MAP = [1, 2, 3, 4, 5, 6, 7];

/* ─── Helpers ───────────────────────────────────────────── */

const getMonday = (d: Date): Date => {
    const date = new Date(d);
    const day = date.getDay();
    date.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
    date.setHours(0, 0, 0, 0);
    return date;
};

const getWeekDates = (monday: Date): Date[] =>
    Array.from({ length: 7 }, (_, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        return d;
    });

const fmtWeekRange = (dates: Date[]) => {
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return `${dates[0].toLocaleDateString('en-US', opts)} – ${dates[6].toLocaleDateString('en-US', opts)}, ${dates[6].getFullYear()}`;
};

const blocksForCell = (blocks: OptBlock[], dow: number, p: Period): OptBlock[] =>
    blocks.filter(b => b.dayOfWeek === dow && b.startHour < p.end && b.endHour > p.start);

/** Find uncovered hour ranges within a period given the overlapping blocks. */
const findGaps = (cellBlocks: OptBlock[], period: Period): { start: number; end: number }[] => {
    const clipped = cellBlocks
        .map(b => ({ start: Math.max(b.startHour, period.start), end: Math.min(b.endHour, period.end) }))
        .sort((a, b) => a.start - b.start);
    const gaps: { start: number; end: number }[] = [];
    let cursor = period.start;
    for (const seg of clipped) {
        if (seg.start > cursor) gaps.push({ start: cursor, end: seg.start });
        cursor = Math.max(cursor, seg.end);
    }
    if (cursor < period.end) gaps.push({ start: cursor, end: period.end });
    return gaps;
};

const fmtGap = (gap: { start: number; end: number }) => {
    const s = gap.start === 0 ? '12AM' : gap.start > 12 ? `${gap.start - 12}PM` : gap.start === 12 ? '12PM' : `${gap.start}AM`;
    const e = gap.end === 24 ? '12AM' : gap.end === 0 ? '12AM' : gap.end > 12 ? `${gap.end - 12}PM` : gap.end === 12 ? '12PM' : `${gap.end}AM`;
    return `${s}–${e}`;
};

const abbrevName = (name: string) => {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    return parts.length === 1 ? parts[0] : `${parts[0][0]}. ${parts.slice(1).join(' ')}`;
};

const fmtPhone = (p: string) => {
    if (!p || p.length < 12) return p;
    return `+1 (${p.substring(2, 5)}) ${p.substring(5, 8)}-${p.substring(8, 12)}`;
};

const fmtHour = (h: number) => {
    const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ap = h < 12 || h === 24 ? 'AM' : 'PM';
    return `${hr}:00 ${ap}`;
};

const isValidPhone = (phone: string) => /^\+1\d{10}$/.test(phone);

const contactsMatch = (a: { full_name: string; phone_number: string }[], b: { full_name: string; phone_number: string }[]) =>
    a.length === b.length &&
    a.every((ac, i) => ac.full_name === b[i].full_name && ac.phone_number === b[i].phone_number);

const shiftColors = (kind: ShiftKind) =>
    kind === 'day'
        ? { bg: GT_COLORS.calDayBg, border: GT_COLORS.calDayBorder, bold: GT_COLORS.calDayText, normal: GT_COLORS.calDayBorder }
        : { bg: GT_COLORS.calNightBg, border: GT_COLORS.calNightBorder, bold: GT_COLORS.calNightText, normal: GT_COLORS.calNightBorder };

const pulseKeyframes = {
    '@keyframes addBlockPulse': {
        '0%': { boxShadow: `0 0 0 0 ${GT_COLORS.primary}88` },
        '70%': { boxShadow: `0 0 0 18px ${GT_COLORS.primary}00` },
        '100%': { boxShadow: `0 0 0 0 ${GT_COLORS.primary}00` },
    },
};

/* ─── On-Call Widget ────────────────────────────────────── */

const OnCallWidget: React.FC<{ blocks: OptBlock[]; timezone: string; hasDefaultContacts?: boolean; defaultContacts?: { full_name: string; phone_number: string }[] }> = ({ blocks, timezone, hasDefaultContacts = false, defaultContacts = [] }) => {
    const now = new Date();
    const jsDay = now.getDay();
    const dow = jsDay === 0 ? 7 : jsDay;
    const hour = now.getHours();

    const current = blocks.find(b => b.dayOfWeek === dow && b.startHour <= hour && b.endHour > hour);

    let next: OptBlock | undefined;
    const sameDayAfter = blocks
        .filter(b => b.dayOfWeek === dow && b.startHour > hour)
        .sort((a, b) => a.startHour - b.startHour);
    if (sameDayAfter.length > 0) {
        next = sameDayAfter[0];
    } else {
        for (let offset = 1; offset <= 7; offset++) {
            const nextDow = ((dow - 1 + offset) % 7) + 1;
            const dayBlocks = blocks.filter(b => b.dayOfWeek === nextDow).sort((a, b) => a.startHour - b.startHour);
            if (dayBlocks.length > 0) { next = dayBlocks[0]; break; }
        }
    }

    const tzLabel = timezone?.replace(/_/g, ' ').split('/').pop() || '';

    return (
        <Box sx={{ display: 'flex', gap: '12px', mb: '16px' }}>
            <Box sx={{
                flex: 1, background: (current || hasDefaultContacts) ? GT_COLORS.greenBg : GT_COLORS.amberBg,
                border: `1px solid ${(current || hasDefaultContacts) ? GT_COLORS.greenBorder : GT_COLORS.amberBorder}`,
                borderRadius: '8px', padding: '12px 16px',
            }}>
                <Box sx={{ fontSize: '10px', fontWeight: 600, color: (current || hasDefaultContacts) ? GT_COLORS.green : GT_COLORS.amber, textTransform: 'uppercase', letterSpacing: '.5px', mb: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: (current || hasDefaultContacts) ? GT_COLORS.green : GT_COLORS.amber }} />
                    {current ? 'On-Call Now' : hasDefaultContacts ? 'Default Contacts' : 'No Coverage'}
                </Box>
                {current ? current.contacts.map((c, i) => (
                    <Box key={i}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{c.full_name}</Typography>
                        <Typography sx={{ fontSize: '11px', color: GT_COLORS.textMuted }}>{fmtPhone(c.phone_number)}</Typography>
                    </Box>
                )) : hasDefaultContacts ? defaultContacts.filter(c => c.full_name.trim()).map((c, i) => (
                    <Box key={i}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{c.full_name}</Typography>
                        <Typography sx={{ fontSize: '11px', color: GT_COLORS.textMuted }}>{fmtPhone(c.phone_number)}</Typography>
                    </Box>
                )) : (
                    <Typography sx={{ fontSize: '12px', color: GT_COLORS.amber }}>No contacts assigned for the current time slot.</Typography>
                )}
                {current && (
                    <Typography sx={{ fontSize: '10px', color: GT_COLORS.textFaint, mt: '6px' }}>
                        Until {current.endHour === 24 ? '12:00 AM' : fmtHour(current.endHour)} {tzLabel} today
                    </Typography>
                )}
            </Box>

            <Box sx={{ flex: 1, background: GT_COLORS.hoverBg, border: `1px solid ${GT_COLORS.border}`, borderRadius: '8px', padding: '12px 16px' }}>
                <Box sx={{ fontSize: '10px', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '.5px', mb: '6px' }}>Next Shift</Box>
                {next ? (
                    <>
                        {next.contacts.map((c, i) => (
                            <Box key={i}>
                                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{c.full_name}</Typography>
                                <Typography sx={{ fontSize: '11px', color: GT_COLORS.textMuted }}>{fmtPhone(c.phone_number)}</Typography>
                            </Box>
                        ))}
                        <Typography sx={{ fontSize: '10px', color: GT_COLORS.textFaint, mt: '6px' }}>
                            {next.dayOfWeek === dow ? 'Today' : DAY_LABELS[(next.dayOfWeek - 1) % 7]} {fmtHour(next.startHour)} – {next.endHour === 24 ? '12:00 AM' : fmtHour(next.endHour)}
                        </Typography>
                    </>
                ) : (
                    <Typography sx={{ fontSize: '12px', color: GT_COLORS.textMuted }}>No upcoming shifts.</Typography>
                )}
            </Box>
        </Box>
    );
};

/* ─── Edit Block Modal ──────────────────────────────────── */

interface EditModalProps {
    open: boolean;
    block: OptBlock | null;
    siblingDays: number[];
    period: Period | null;
    teamName: string;
    timezone: string;
    onClose: () => void;
    onSave: (update: { days: number[]; startHour: number; endHour: number; contacts: { full_name: string; phone_number: string }[] }) => void;
    onDelete: () => void;
}

const EditBlockModal: React.FC<EditModalProps> = ({ open, block, siblingDays, period, teamName, timezone, onClose, onSave, onDelete }) => {
    const [selectedDays, setSelectedDays] = useState<number[]>([1]);
    const [startHour, setStartHour] = useState(0);
    const [endHour, setEndHour] = useState(24);
    const [c1Name, setC1Name] = useState('');
    const [c1Phone, setC1Phone] = useState('+1');
    const [c2Name, setC2Name] = useState('');
    const [c2Phone, setC2Phone] = useState('+1');
    const [error, setError] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (block) {
            setSelectedDays(siblingDays.length > 0 ? siblingDays : [block.dayOfWeek]);
            setStartHour(block.startHour);
            setEndHour(block.endHour);
            const c0 = block.contacts[0] || { full_name: '', phone_number: '+1' };
            const c1 = block.contacts[1] || { full_name: '', phone_number: '+1' };
            setC1Name(c0.full_name); setC1Phone(c0.phone_number || '+1');
            setC2Name(c1.full_name); setC2Phone(c1.phone_number || '+1');
            setError('');
            setConfirmDelete(false);
        }
    }, [block, siblingDays]);

    const toggleDay = (dow: number) => {
        setSelectedDays(prev => {
            if (prev.includes(dow)) {
                if (prev.length === 1) return prev; // must keep at least one
                return prev.filter(d => d !== dow);
            }
            return [...prev, dow].sort();
        });
    };

    const enforcePhone = (val: string) => {
        if (!val.startsWith('+1')) return '+1';
        const digits = val.replace(/[^\d]/g, '');
        return '+1' + digits.substring(1, 11);
    };

    const tzLabel = timezone?.replace(/_/g, ' ').split('/').pop() || '';

    const dayBtns = [
        { label: 'M', dow: 1 }, { label: 'T', dow: 2 }, { label: 'W', dow: 3 },
        { label: 'TH', dow: 4 }, { label: 'F', dow: 5 }, { label: 'SA', dow: 6 }, { label: 'S', dow: 7 },
    ];

    const handleSave = () => {
        if (selectedDays.length === 0) { setError('Select at least one day.'); return; }
        if (endHour <= startHour) { setError('End time must be after start time.'); return; }
        if (!c1Name.trim()) { setError('At least one contact name is required.'); return; }
        if (!isValidPhone(c1Phone)) { setError('Contact 1 phone must be +1 followed by 10 digits.'); return; }
        if (c2Name.trim() && !isValidPhone(c2Phone)) { setError('Contact 2 phone must be +1 followed by 10 digits.'); return; }
        setError('');
        const contacts = [{ full_name: c1Name.trim(), phone_number: c1Phone }];
        if (c2Name.trim()) contacts.push({ full_name: c2Name.trim(), phone_number: c2Phone });
        onSave({ days: selectedDays, startHour, endHour, contacts });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pr: 2 }}>
                <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>Edit Schedule Block</DialogTitle>
                <IconButton color="error" onClick={() => setConfirmDelete(true)} size="small">
                    <DeleteOutlineIcon sx={{ fontSize: 22 }} />
                </IconButton>
            </Stack>
            <DialogContent>
                <Stack spacing={2}>
                    <Box sx={{ background: GT_COLORS.primaryLight, border: '1px solid #D4E3F0', borderRadius: '8px', p: '12px 16px', fontSize: '12px', color: GT_COLORS.primary }}>
                        <span style={{ color: GT_COLORS.textMuted }}>{teamName} · {tzLabel}</span>
                    </Box>

                    {/* Day selector — multi-select */}
                    <Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '8px' }}>Days</Typography>
                        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {dayBtns.map(d => {
                                const sel = selectedDays.includes(d.dow);
                                return (
                                <Box key={d.dow} onClick={() => toggleDay(d.dow)} sx={{
                                    width: 40, height: 40, borderRadius: '50%',
                                    border: `1px solid ${sel ? GT_COLORS.primary : GT_COLORS.borderInput}`,
                                    background: sel ? GT_COLORS.primary : '#fff',
                                    color: sel ? '#fff' : GT_COLORS.textSecondary,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all .15s',
                                    '&:hover': { borderColor: GT_COLORS.primary, color: sel ? '#fff' : GT_COLORS.primary },
                                }}>{d.label}</Box>
                                );
                            })}
                        </Box>
                    </Box>

                    {/* Time selectors */}
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '6px' }}>Start Time</Typography>
                            <select value={startHour} onChange={e => setStartHour(Number(e.target.value))}
                                style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${GT_COLORS.borderInput}`, fontSize: 13, fontFamily: 'Poppins' }}>
                                {HOUR_OPTIONS.map(h => <option key={h} value={h}>{fmtHour(h)}</option>)}
                            </select>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '6px' }}>End Time</Typography>
                            <select value={endHour} onChange={e => setEndHour(Number(e.target.value))}
                                style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${GT_COLORS.borderInput}`, fontSize: 13, fontFamily: 'Poppins' }}>
                                {END_HOUR_OPTIONS.map(h => <option key={h} value={h}>{fmtHour(h)}</option>)}
                            </select>
                        </Box>
                    </Stack>

                    {/* Contacts */}
                    <Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '6px' }}>Emergency Contact 1</Typography>
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Full Name" value={c1Name} onChange={e => setC1Name(e.target.value)} fullWidth />
                            <TextField size="small" label="Phone" value={c1Phone} onChange={e => setC1Phone(enforcePhone(e.target.value))} fullWidth />
                        </Stack>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '6px' }}>Emergency Contact 2</Typography>
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Full Name" value={c2Name} onChange={e => setC2Name(e.target.value)} fullWidth />
                            <TextField size="small" label="Phone" value={c2Phone} onChange={e => setC2Phone(enforcePhone(e.target.value))} fullWidth />
                        </Stack>
                        <Typography sx={{ fontSize: '11px', color: GT_COLORS.textMuted, mt: '4px' }}>
                            1 Emergency Contact is required. Dispatch will always contact the first entry and attempt the second if needed.
                        </Typography>
                    </Box>
                    {error && <Typography sx={{ color: GT_COLORS.red, fontSize: '12px' }}>{error}</Typography>}
                </Stack>

                {/* Delete confirmation */}
                <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
                    <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>Delete Schedule Block</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontSize: '13px' }}>
                            {siblingDays.length > 1
                                ? `This will delete schedule blocks for ${siblingDays.map(d => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d - 1]).join(', ')}. This cannot be undone.`
                                : 'Are you sure you want to delete this schedule block? This cannot be undone.'}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDelete(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                        <Button color="error" variant="contained" onClick={() => { setConfirmDelete(false); onDelete(); }} sx={{ textTransform: 'none' }}>Delete</Button>
                    </DialogActions>
                </Dialog>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} sx={{ textTransform: 'none', backgroundColor: GT_COLORS.primary, '&:hover': { backgroundColor: GT_COLORS.primaryHover } }}>Save Changes</Button>
            </DialogActions>
        </Dialog>
    );
};

/* ─── Create Block Modal ────────────────────────────────── */

interface CreateModalProps {
    open: boolean;
    onClose: () => void;
    onCreate: (days: number[], startHour: number, endHour: number, contacts: { full_name: string; phone_number: string }[]) => void;
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);
const END_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i + 1);

const CreateBlockModal: React.FC<CreateModalProps> = ({ open, onClose, onCreate }) => {
    const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]);
    const [startHour, setStartHour] = useState(17);
    const [endHour, setEndHour] = useState(24);
    const [c1Name, setC1Name] = useState('');
    const [c1Phone, setC1Phone] = useState('+1');
    const [c2Name, setC2Name] = useState('');
    const [c2Phone, setC2Phone] = useState('+1');
    const [error, setError] = useState('');

    const dayBtns = [
        { label: 'M', dow: 1 }, { label: 'T', dow: 2 }, { label: 'W', dow: 3 },
        { label: 'TH', dow: 4 }, { label: 'F', dow: 5 }, { label: 'SA', dow: 6 }, { label: 'S', dow: 7 },
    ];

    const toggleDay = (dow: number) =>
        setSelectedDays(prev => prev.includes(dow) ? prev.filter(d => d !== dow) : [...prev, dow]);

    const enforcePhone = (val: string) => {
        if (!val.startsWith('+1')) return '+1';
        const digits = val.replace(/[^\d]/g, '');
        return '+1' + digits.substring(1, 11);
    };

    const handleCreate = () => {
        if (selectedDays.length === 0) { setError('Select at least one day.'); return; }
        if (endHour <= startHour) { setError('End time must be after start time.'); return; }
        if (!c1Name.trim()) { setError('At least one contact name is required.'); return; }
        if (!isValidPhone(c1Phone)) { setError('Contact 1 phone must be +1 followed by 10 digits.'); return; }
        if (c2Name.trim() && !isValidPhone(c2Phone)) { setError('Contact 2 phone must be +1 followed by 10 digits.'); return; }
        setError('');
        const contacts = [{ full_name: c1Name.trim(), phone_number: c1Phone }];
        if (c2Name.trim()) contacts.push({ full_name: c2Name.trim(), phone_number: c2Phone });
        onCreate(selectedDays, startHour, endHour, contacts);
    };

    useEffect(() => {
        if (open) {
            setSelectedDays([1, 2, 3, 4, 5]); setStartHour(17); setEndHour(24);
            setC1Name(''); setC1Phone('+1'); setC2Name(''); setC2Phone('+1'); setError('');
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>Add Schedule Block</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '8px' }}>Days</Typography>
                        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {dayBtns.map(d => (
                                <Box key={d.dow} onClick={() => toggleDay(d.dow)} sx={{
                                    width: 40, height: 40, borderRadius: '50%',
                                    border: `1px solid ${selectedDays.includes(d.dow) ? GT_COLORS.primary : GT_COLORS.borderInput}`,
                                    background: selectedDays.includes(d.dow) ? GT_COLORS.primary : '#fff',
                                    color: selectedDays.includes(d.dow) ? '#fff' : GT_COLORS.textSecondary,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all .15s',
                                    '&:hover': { borderColor: GT_COLORS.primary, color: selectedDays.includes(d.dow) ? '#fff' : GT_COLORS.primary },
                                }}>{d.label}</Box>
                            ))}
                        </Box>
                    </Box>

                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '6px' }}>Start Time</Typography>
                            <select value={startHour} onChange={e => setStartHour(Number(e.target.value))}
                                style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${GT_COLORS.borderInput}`, fontSize: 13, fontFamily: 'Poppins' }}>
                                {HOUR_OPTIONS.map(h => <option key={h} value={h}>{fmtHour(h)}</option>)}
                            </select>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '6px' }}>End Time</Typography>
                            <select value={endHour} onChange={e => setEndHour(Number(e.target.value))}
                                style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${GT_COLORS.borderInput}`, fontSize: 13, fontFamily: 'Poppins' }}>
                                {END_HOUR_OPTIONS.map(h => <option key={h} value={h}>{fmtHour(h)}</option>)}
                            </select>
                        </Box>
                    </Stack>

                    <Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '6px' }}>Emergency Contact 1</Typography>
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Full Name" value={c1Name} onChange={e => setC1Name(e.target.value)} fullWidth />
                            <TextField size="small" label="Phone" value={c1Phone} onChange={e => setC1Phone(enforcePhone(e.target.value))} fullWidth />
                        </Stack>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '6px' }}>Emergency Contact 2</Typography>
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Full Name" value={c2Name} onChange={e => setC2Name(e.target.value)} fullWidth />
                            <TextField size="small" label="Phone" value={c2Phone} onChange={e => setC2Phone(enforcePhone(e.target.value))} fullWidth />
                        </Stack>
                        <Typography sx={{ fontSize: '11px', color: GT_COLORS.textMuted, mt: '4px' }}>
                            1 Emergency Contact is required. Dispatch will always contact the first entry and attempt the second if needed.
                        </Typography>
                    </Box>
                    {error && <Typography sx={{ color: GT_COLORS.red, fontSize: '12px' }}>{error}</Typography>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" onClick={handleCreate} sx={{ textTransform: 'none', backgroundColor: GT_COLORS.primary, '&:hover': { backgroundColor: GT_COLORS.primaryHover } }}>Create Block</Button>
            </DialogActions>
        </Dialog>
    );
};

/* ─── Main Component ────────────────────────────────────── */

export const EmergencyScheduleSection: React.FC<EmergencyScheduleSectionProps> = React.memo(({ teamId, teamName, defaultContacts = [] }) => {
    const hasDefaultContacts = defaultContacts.some(c => c.full_name.trim().length > 0 && c.phone_number.replace(/[^0-9]/g, '').length === 11);
    const { pagination, loading } = useSelector((s: RootState) => s.schedule);
    const [fetched, setFetched] = useState(false);
    const [creating, setCreating] = useState(false);
    const [weekMonday, setWeekMonday] = useState(() => getMonday(new Date()));
    const weekDates = useMemo(() => getWeekDates(weekMonday), [weekMonday]);

    const [editOpen, setEditOpen] = useState(false);
    const [editBlock, setEditBlock] = useState<OptBlock | null>(null);
    const [editSiblingDays, setEditSiblingDays] = useState<number[]>([]);
    const [editPeriod, setEditPeriod] = useState<Period | null>(null);
    const [createOpen, setCreateOpen] = useState(false);

    const refetch = () => ScheduleService.fetchMany({ page: 1, perPage: 200 });

    useEffect(() => {
        setFetched(false);
        ScheduleService.fetchMany({ page: 1, perPage: 200 }, () => setFetched(true));
    }, [teamId]);

    const items = pagination.items || [];
    const schedule: ScheduleType.Model | null = items.find((s) => s.teamId === teamId) || null;
    const blocks = schedule?.optimizedBlocks || [];
    const isLoading = loading.fetchMany || !fetched;

    const handleCreateSchedule = async () => {
        setCreating(true);
        try {
            await ScheduleService.createSchedule(teamId, (ok, msg) => {
                setCreating(false);
                if (ok) { toast.success('Emergency schedule created.'); refetch(); }
                else toast.error(msg || 'Failed to create schedule.');
            });
        } catch {
            setCreating(false);
            toast.error('Failed to create schedule.');
        }
    };

    const handleEditSave = (update: { days: number[]; startHour: number; endHour: number; contacts: { full_name: string; phone_number: string }[] }) => {
        if (!schedule || !editBlock) return;
        const updated: ScheduleType.Model = structuredClone(schedule);

        // Remove all sibling blocks (same time + contacts as the original block)
        const siblingIds = new Set(
            blocks.filter(b =>
                b.startHour === editBlock.startHour &&
                b.endHour === editBlock.endHour &&
                contactsMatch(b.contacts, editBlock.contacts)
            ).map(b => b.id)
        );
        updated.optimizedBlocks = updated.optimizedBlocks.filter((b: OptBlock) => !siblingIds.has(b.id));

        // Add blocks for each selected day, skipping if an unrelated block already covers that slot
        const skippedDays: number[] = [];
        for (const dow of update.days) {
            const overlap = updated.optimizedBlocks.some((b: OptBlock) =>
                b.dayOfWeek === dow && b.startHour < update.endHour && b.endHour > update.startHour,
            );
            if (!overlap) {
                updated.optimizedBlocks.push({
                    id: `${dow}-${update.startHour}-${update.endHour}`,
                    dayOfWeek: dow,
                    startHour: update.startHour,
                    endHour: update.endHour,
                    contacts: update.contacts,
                });
            } else {
                skippedDays.push(dow);
            }
        }
        // Restore original sibling blocks for skipped days so they aren't silently deleted
        for (const dow of skippedDays) {
            const original = blocks.find(b => siblingIds.has(b.id) && b.dayOfWeek === dow);
            if (original) updated.optimizedBlocks.push(original);
        }

        if (skippedDays.length === update.days.length) {
            toast.error('All selected days have overlapping blocks. No changes saved.');
            return;
        }
        if (skippedDays.length > 0) {
            const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
            const names = skippedDays.map(d => dayNames[d - 1]).join(', ');
            toast.warn(`Skipped ${names} — overlapping block exists. Original blocks preserved.`);
        }

        ScheduleService.updateSchedule(updated, (ok) => {
            if (ok) { toast.success('Schedule block updated.'); setEditOpen(false); refetch(); }
            else toast.error('Failed to update block.');
        });
    };

    const handleEditDelete = () => {
        if (!schedule || !editBlock) return;
        const updated: ScheduleType.Model = structuredClone(schedule);

        // Delete all sibling blocks (same time + contacts)
        const siblingIds = new Set(
            blocks.filter(b =>
                b.startHour === editBlock.startHour &&
                b.endHour === editBlock.endHour &&
                contactsMatch(b.contacts, editBlock.contacts)
            ).map(b => b.id)
        );
        updated.optimizedBlocks = updated.optimizedBlocks.filter((b: OptBlock) => !siblingIds.has(b.id));

        ScheduleService.updateSchedule(updated, (ok) => {
            if (ok) { toast.success('Schedule block deleted.'); setEditOpen(false); refetch(); }
            else toast.error('Failed to delete block.');
        });
    };

    const handleCreateBlock = (days: number[], startHour: number, endHour: number, contacts: { full_name: string; phone_number: string }[]) => {
        if (!schedule) return;
        const updated: ScheduleType.Model = structuredClone(schedule);
        const skippedDays: number[] = [];
        for (const dow of days) {
            const overlap = updated.optimizedBlocks.some((b: OptBlock) =>
                b.dayOfWeek === dow && b.startHour < endHour && b.endHour > startHour,
            );
            if (!overlap) {
                updated.optimizedBlocks.push({
                    id: `${dow}-${startHour}-${endHour}`,
                    dayOfWeek: dow, startHour, endHour, contacts,
                });
            } else {
                skippedDays.push(dow);
            }
        }
        if (skippedDays.length === days.length) {
            toast.error('All selected days have overlapping blocks. No blocks created.');
            return;
        }
        if (skippedDays.length > 0) {
            const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
            toast.warn(`Skipped ${skippedDays.map(d => dayNames[d - 1]).join(', ')} — overlapping block exists.`);
        }
        ScheduleService.updateSchedule(updated, (ok) => {
            if (ok) { toast.success('Schedule block created.'); setCreateOpen(false); refetch(); }
            else toast.error('Failed to create block.');
        });
    };

    const handleCellClick = (dow: number, period: Period) => {
        const cellBlocks = blocksForCell(blocks, dow, period);
        if (cellBlocks.length > 0) {
            const clicked = cellBlocks[0];
            // Find all days that have the same time range + contacts
            const siblings = blocks
                .filter(b =>
                    b.startHour === clicked.startHour &&
                    b.endHour === clicked.endHour &&
                    contactsMatch(b.contacts, clicked.contacts)
                )
                .map(b => b.dayOfWeek)
                .sort();
            setEditBlock(clicked);
            setEditSiblingDays(siblings);
            setEditPeriod(period);
            setEditOpen(true);
        }
    };

    const prevWeek = () => { const d = new Date(weekMonday); d.setDate(d.getDate() - 7); setWeekMonday(d); };
    const nextWeek = () => { const d = new Date(weekMonday); d.setDate(d.getDate() + 7); setWeekMonday(d); };
    const goToday = () => setWeekMonday(getMonday(new Date()));

    return (
        <Box sx={{ backgroundColor: GT_COLORS.cardBg, border: `1px solid ${GT_COLORS.border}`, borderRadius: '10px', padding: '20px' }}>

            {/* Header */}
            <Box sx={{ borderBottom: `1px solid ${GT_COLORS.border}`, pb: '14px', mb: '14px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <CalendarMonthIcon sx={{ fontSize: '16px', color: GT_COLORS.primary }} />
                            <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Emergency Contact Schedule</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '11px', color: GT_COLORS.textMuted, mt: '2px' }}>
                            Define who Dispatch calls and when. Schedule time blocks with up to 2 contacts per block.
                        </Typography>
                    </Box>
                    {schedule && (
                        <Button size="small" startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />} onClick={() => setCreateOpen(true)}
                            sx={{
                                textTransform: 'none', fontSize: '12px', borderRadius: '6px', px: '12px',
                                ...(blocks.length === 0
                                    ? {
                                        ...pulseKeyframes,
                                        backgroundColor: GT_COLORS.primary,
                                        color: '#fff',
                                        border: `1px solid ${GT_COLORS.primary}`,
                                        animation: 'addBlockPulse 2s ease-in-out infinite',
                                        fontWeight: 600,
                                        '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                                    }
                                    : {
                                        color: GT_COLORS.textSecondary,
                                        border: `1px solid ${GT_COLORS.borderInput}`,
                                    }
                                ),
                            }}>
                            Add Block
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Loading */}
            {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}

            {/* Empty State */}
            {!isLoading && !schedule && (
                <Box sx={{ border: `1px dashed ${GT_COLORS.border}`, borderRadius: '8px', padding: '32px', textAlign: 'center' }}>
                    <CalendarMonthIcon sx={{ fontSize: '40px', color: GT_COLORS.textDisabled, mb: '8px' }} />
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: '4px' }}>No emergency schedule configured</Typography>
                    <Typography sx={{ fontSize: '13px', color: GT_COLORS.textFaint, mb: '16px' }}>
                        Set up a weekly on-call schedule so Dispatch knows who to contact when a timer expires.
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateSchedule} disabled={creating}
                        sx={{ backgroundColor: GT_COLORS.primary, textTransform: 'none', fontSize: '14px', fontWeight: 500, borderRadius: '8px', '&:hover': { backgroundColor: GT_COLORS.primaryHover } }}>
                        {creating ? 'Creating...' : 'Set Up Schedule'}
                    </Button>
                </Box>
            )}

            {/* Schedule Content */}
            {!isLoading && schedule && (
                <>
                    <OnCallWidget blocks={blocks} timezone={schedule.timezone} hasDefaultContacts={hasDefaultContacts} defaultContacts={defaultContacts} />

                    <Box sx={{ border: `1px solid ${GT_COLORS.border}`, borderRadius: '8px', overflow: 'hidden' }}>
                        {/* Week Nav */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '16px', py: '10px', background: GT_COLORS.headerBg, borderBottom: `1px solid ${GT_COLORS.border}` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <IconButton size="small" onClick={prevWeek} sx={{ border: `1px solid ${GT_COLORS.borderInput}`, borderRadius: '6px', p: '4px 8px' }}>
                                    <ChevronLeftIcon sx={{ fontSize: 16, color: GT_COLORS.textMuted }} />
                                </IconButton>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{fmtWeekRange(weekDates)}</Typography>
                                <IconButton size="small" onClick={nextWeek} sx={{ border: `1px solid ${GT_COLORS.borderInput}`, borderRadius: '6px', p: '4px 8px' }}>
                                    <ChevronRightIcon sx={{ fontSize: 16, color: GT_COLORS.textMuted }} />
                                </IconButton>
                            </Box>
                            <Button size="small" onClick={goToday}
                                sx={{ border: `1px solid ${GT_COLORS.borderInput}`, borderRadius: '6px', px: '10px', fontSize: '12px', color: GT_COLORS.primary, fontWeight: 500, textTransform: 'none', minWidth: 'auto' }}>
                                Today
                            </Button>
                        </Box>

                        {/* Grid */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)', minHeight: 280 }}>
                            {/* Header corner */}
                            <Box sx={{ borderBottom: `1px solid ${GT_COLORS.border}`, borderRight: `1px solid ${GT_COLORS.border}`, p: '8px 4px', background: GT_COLORS.headerBg }} />
                            {/* Header day cells */}
                            {weekDates.map((date, i) => (
                                <Box key={i} sx={{
                                    borderBottom: `1px solid ${GT_COLORS.border}`,
                                    borderRight: i < 6 ? `1px solid ${GT_COLORS.borderLight}` : 'none',
                                    p: '8px 6px', textAlign: 'center', background: GT_COLORS.headerBg,
                                }}>
                                    <Typography sx={{ fontSize: '12px', color: GT_COLORS.textFaint, fontWeight: 500 }}>{DAY_LABELS[i]}</Typography>
                                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: i >= 5 ? GT_COLORS.textMuted : GT_COLORS.textPrimary }}>{date.getDate()}</Typography>
                                </Box>
                            ))}

                            {/* Period rows */}
                            {PERIODS.map((period, pIdx) => (
                                <React.Fragment key={pIdx}>
                                    <Box sx={{
                                        borderRight: `1px solid ${GT_COLORS.border}`,
                                        borderBottom: pIdx < 2 ? `1px solid ${GT_COLORS.borderLight}` : 'none',
                                        p: '6px 4px', textAlign: 'center', fontSize: '12px', color: GT_COLORS.textFaint,
                                        background: GT_COLORS.headerBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        {period.label}
                                    </Box>
                                    {DOW_MAP.map((dow, dIdx) => {
                                        const cellBlocks = blocksForCell(blocks, dow, period);
                                        const has = cellBlocks.length > 0;
                                        const colors = shiftColors(period.shift);
                                        const gaps = has ? findGaps(cellBlocks, period) : [];
                                        return (
                                            <Box key={dIdx} onClick={() => handleCellClick(dow, period)} sx={{
                                                borderRight: dIdx < 6 ? `1px solid ${GT_COLORS.borderLight}` : 'none',
                                                borderBottom: pIdx < 2 ? `1px solid ${GT_COLORS.borderLight}` : 'none',
                                                p: '4px', cursor: has ? 'pointer' : 'default',
                                                background: has ? 'transparent' : hasDefaultContacts ? GT_COLORS.primaryLight : GT_COLORS.amberBg,
                                                transition: 'background .1s',
                                            }}>
                                                {has ? (
                                                    <Box sx={{
                                                        background: gaps.length > 0 ? `linear-gradient(to bottom, ${colors.bg} 0%, ${colors.bg} 70%, ${GT_COLORS.amberBg} 100%)` : colors.bg,
                                                        borderLeft: `3px solid ${gaps.length > 0 ? GT_COLORS.amber : colors.border}`,
                                                        borderRadius: '4px', p: '6px 8px', fontSize: '12px', height: '100%', minHeight: 52,
                                                        '&:hover': { opacity: 0.85 },
                                                    }}>
                                                        {cellBlocks[0].contacts.map((c, ci) => (
                                                            <Typography key={ci} sx={{
                                                                fontSize: '12px', fontWeight: ci === 0 ? 600 : 400,
                                                                color: ci === 0 ? colors.bold : colors.normal,
                                                            }}>{abbrevName(c.full_name)}</Typography>
                                                        ))}
                                                        {gaps.length > 0 && (
                                                            <Typography sx={{ fontSize: '9px', color: GT_COLORS.amberDark, fontWeight: 600, mt: '4px' }}>
                                                                ⚠ Gap: {gaps.map(g => fmtGap(g)).join(', ')}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ fontSize: '11px', color: hasDefaultContacts ? GT_COLORS.primary : GT_COLORS.amber, textAlign: 'center', fontWeight: 500, minHeight: 52, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        {hasDefaultContacts ? 'Default' : 'No coverage'}
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </Box>

                        {/* Legend */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', px: '16px', py: '10px', background: GT_COLORS.headerBg, borderTop: `1px solid ${GT_COLORS.border}`, fontSize: '11px', color: GT_COLORS.textMuted }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '2px', background: GT_COLORS.calDayBg, borderLeft: `3px solid ${GT_COLORS.calDayBorder}` }} /> Day Shift
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '2px', background: GT_COLORS.calNightBg, borderLeft: `3px solid ${GT_COLORS.calNightBorder}` }} /> Overnight
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '2px', background: hasDefaultContacts ? GT_COLORS.primaryLight : GT_COLORS.amberBg, border: `1px solid ${hasDefaultContacts ? GT_COLORS.primary : GT_COLORS.amberBorder}` }} />
                                {hasDefaultContacts ? 'Default Contacts' : 'No Coverage'}
                            </Box>
                            <Box sx={{ flex: 1 }} />
                            <Typography sx={{ fontSize: '11px', color: GT_COLORS.textMuted }}>Click any block to edit contacts</Typography>
                        </Box>
                    </Box>
                </>
            )}

            {/* Modals */}
            <EditBlockModal open={editOpen} block={editBlock} siblingDays={editSiblingDays} period={editPeriod} teamName={teamName} timezone={schedule?.timezone || ''} onClose={() => { setEditOpen(false); setEditBlock(null); setEditSiblingDays([]); }} onSave={handleEditSave} onDelete={handleEditDelete} />
            <CreateBlockModal open={createOpen} onClose={() => setCreateOpen(false)} onCreate={handleCreateBlock} />
        </Box>
    );
});
